import React from 'react'

import { CheckCircle, Clock, RotateCw, XCircle } from 'lucide-react'
import Modal from 'react-modal'

import { getChainName, getChainNameFromChainId } from '@src/configs/chainInfo'
import { IExchangeData } from '@src/types/exchange'
import { formatExchangeAmountWithCommas } from '@src/utils'

/**
 * Enum representing the different states of a Feather order
 * These are the frontend representations of the different backend states
 */
export enum FeatherOrderStatus {
    UNKNOWN = 'unknown',
    ORDER_RECEIVED = 'order_received',
    ORDER_PROCESSING = 'order_processing',
    AWAITING_DEPOSIT = 'awaiting_deposit',
    DEPOSIT_TIMEOUT = 'deposit_timeout',
    DEPOSIT_CONFIRMED = 'deposit_confirmed',
    TRADING = 'trade_in_progress',
    TRADE_FAILED = 'trade_failed',
    TRADED = 'trade_completed',
    WITHDRAWING = 'withdrawal_in_progress',
    WITHDRAWAL_FAILED = 'withdrawal_failed',
    WITHDRAWAL_PENDING = 'withdrawal_pending_tx',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export interface IFeatherOrderStatusProps {
    isOpen: boolean
    onClose: () => void
    sellData: IExchangeData
    buyData: IExchangeData
    src_deposit_tx: string | null | undefined
    dst_fill_tx: string | null | undefined
    status: FeatherOrderStatus | undefined
    orderId: string | undefined
}

export const FeatherOrderStatusModal: React.FC<IFeatherOrderStatusProps> = ({
    isOpen,
    onClose,
    sellData,
    buyData,
    src_deposit_tx,
    dst_fill_tx,
    status = FeatherOrderStatus.ORDER_RECEIVED,
    orderId,
}) => {
    const sellAmount = formatExchangeAmountWithCommas(sellData)
    const buyAmount = formatExchangeAmountWithCommas(buyData)
    const sellChainName = getChainName(getChainNameFromChainId(sellData.chainId))
    const buyChainName = getChainName(getChainNameFromChainId(buyData.chainId))

    const getStatusIcon = () => {
        switch (status) {
            case FeatherOrderStatus.AWAITING_DEPOSIT:
                return <Clock className='h-16 w-16 text-yellow-500' />
            case FeatherOrderStatus.DEPOSIT_CONFIRMED:
            case FeatherOrderStatus.ORDER_RECEIVED:
            case FeatherOrderStatus.ORDER_PROCESSING:
            case FeatherOrderStatus.TRADING:
            case FeatherOrderStatus.TRADED:
            case FeatherOrderStatus.WITHDRAWING:
            case FeatherOrderStatus.WITHDRAWAL_PENDING:
                return <RotateCw className='h-16 w-16 animate-spin text-blue-500' />
            case FeatherOrderStatus.COMPLETED:
                return <CheckCircle className='h-16 w-16 text-green-500' />
            case FeatherOrderStatus.DEPOSIT_TIMEOUT:
            case FeatherOrderStatus.TRADE_FAILED:
            case FeatherOrderStatus.WITHDRAWAL_FAILED:
            case FeatherOrderStatus.FAILED:
                return <XCircle className='h-16 w-16 text-red-500' />
            case FeatherOrderStatus.UNKNOWN:
            default:
                return <Clock className='h-16 w-16 text-gray-500' />
        }
    }

    const getStatusTitle = () => {
        switch (status) {
            case FeatherOrderStatus.UNKNOWN:
                return 'Order Status Unknown'
            case FeatherOrderStatus.ORDER_RECEIVED:
                return 'Order Received'
            case FeatherOrderStatus.ORDER_PROCESSING:
                return 'Processing Order'
            case FeatherOrderStatus.AWAITING_DEPOSIT:
                return 'Waiting for Deposit'
            case FeatherOrderStatus.DEPOSIT_TIMEOUT:
                return 'Deposit Timed Out'
            case FeatherOrderStatus.DEPOSIT_CONFIRMED:
                return 'Deposit Confirmed'
            case FeatherOrderStatus.TRADING:
                return 'Exchange in Progress'
            case FeatherOrderStatus.TRADE_FAILED:
                return 'Exchange Failed'
            case FeatherOrderStatus.TRADED:
                return 'Exchange Completed'
            case FeatherOrderStatus.WITHDRAWING:
                return 'Sending to Your Wallet'
            case FeatherOrderStatus.WITHDRAWAL_FAILED:
                return 'Withdrawal Failed'
            case FeatherOrderStatus.WITHDRAWAL_PENDING:
                return 'Waiting for Transaction Confirmation'
            case FeatherOrderStatus.COMPLETED:
                return 'Order Completed'
            case FeatherOrderStatus.FAILED:
                return 'Order Failed'
            default:
                return 'Unknown Status'
        }
    }

    const getStatusDescription = () => {
        switch (status) {
            case FeatherOrderStatus.UNKNOWN:
                return 'The status of your order is currently unknown. Please contact support if this persists.'
            case FeatherOrderStatus.ORDER_RECEIVED:
                return 'Your order has been received and is being initialized.'
            case FeatherOrderStatus.ORDER_PROCESSING:
                return 'Your order is being processed. Please wait while we prepare for your deposit.'
            case FeatherOrderStatus.AWAITING_DEPOSIT:
                return `Please send ${sellAmount} ${sellData.token.symbol} to the deposit address to begin the exchange.`
            case FeatherOrderStatus.DEPOSIT_TIMEOUT:
                return `We didn't receive your deposit in time. If you've already sent funds, please contact support.`
            case FeatherOrderStatus.DEPOSIT_CONFIRMED:
                return `We've confirmed your deposit of ${sellAmount} ${sellData.token.symbol}. Your exchange will begin shortly.`
            case FeatherOrderStatus.TRADING:
                return `We're exchanging your ${sellData.token.symbol} for ${buyData.token.symbol}. This may take a few minutes.`
            case FeatherOrderStatus.TRADE_FAILED:
                return `There was an issue during the exchange process. Please contact support.`
            case FeatherOrderStatus.TRADED:
                return `The exchange of ${sellData.token.symbol} to ${buyData.token.symbol} is complete. Now preparing withdrawal.`
            case FeatherOrderStatus.WITHDRAWING:
                return `We're sending ${buyAmount} ${buyData.token.symbol} to your ${buyChainName} wallet.`
            case FeatherOrderStatus.WITHDRAWAL_FAILED:
                return `We had an issue sending funds to your wallet. Please contact support.`
            case FeatherOrderStatus.WITHDRAWAL_PENDING:
                return `Your transaction has been submitted to the ${buyChainName} network and is awaiting confirmation.`
            case FeatherOrderStatus.COMPLETED:
                return `Your order is complete! ${buyAmount} ${buyData.token.symbol} has been sent to your ${buyChainName} wallet.`
            case FeatherOrderStatus.FAILED:
                return 'We encountered an issue processing your order. Please contact support.'
            default:
                return 'Status information unavailable.'
        }
    }

    // Define progress steps and their completion state
    const steps = [
        {
            name: 'Deposit',
            completed: ![
                FeatherOrderStatus.UNKNOWN,
                FeatherOrderStatus.ORDER_RECEIVED,
                FeatherOrderStatus.ORDER_PROCESSING,
                FeatherOrderStatus.AWAITING_DEPOSIT,
            ].includes(status),
        },
        {
            name: 'Processing',
            completed: [
                FeatherOrderStatus.TRADING,
                FeatherOrderStatus.TRADED,
                FeatherOrderStatus.WITHDRAWING,
                FeatherOrderStatus.WITHDRAWAL_PENDING,
                FeatherOrderStatus.COMPLETED,
            ].includes(status),
        },
        {
            name: 'Sending',
            completed: [
                FeatherOrderStatus.WITHDRAWING,
                FeatherOrderStatus.WITHDRAWAL_PENDING,
                FeatherOrderStatus.COMPLETED,
            ].includes(status),
        },
        {
            name: 'Complete',
            completed: status === FeatherOrderStatus.COMPLETED,
        },
    ]

    const isFailed = [
        FeatherOrderStatus.DEPOSIT_TIMEOUT,
        FeatherOrderStatus.TRADE_FAILED,
        FeatherOrderStatus.WITHDRAWAL_FAILED,
        FeatherOrderStatus.FAILED,
    ].includes(status)

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={{
                overlay: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 50,
                    padding: '16px',
                },
                content: {
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '576px',
                    height: '650px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '24px',
                    background: '#17191C',
                    overflow: 'auto',
                },
            }}
            ariaHideApp={false}
        >
            <div className='flex h-full flex-col'>
                {/* Header */}
                <div className='flex items-center justify-between border-b border-white/10 px-6 py-4'>
                    <h2 className='text-xl font-medium'>Feather Order Status</h2>
                    <button onClick={onClose} className='text-gray-400 hover:text-white'>
                        <XCircle />
                    </button>
                </div>

                {/* Content */}
                <div className='flex-1 overflow-auto p-6'>
                    <div className='mb-8 flex flex-col items-center'>
                        {getStatusIcon()}
                        <h2 className='mt-4 text-2xl font-medium'>{getStatusTitle()}</h2>
                        <p className='mt-2 max-w-md text-center text-gray-400'>{getStatusDescription()}</p>
                    </div>

                    {!isFailed && (
                        <div className='mb-8'>
                            <div className='relative mb-8'>
                                <div className='absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 bg-gray-700'></div>
                                <div className='relative flex justify-between'>
                                    {steps.map((step, i) => (
                                        <div key={i} className='flex flex-col items-center'>
                                            <div
                                                className={`z-10 flex h-8 w-8 items-center justify-center rounded-full ${step.completed ? 'bg-blue-600' : 'bg-gray-700'}`}
                                            >
                                                {step.completed ? (
                                                    <CheckCircle className='h-5 w-5 text-white' />
                                                ) : (
                                                    <span className='text-white'>{i + 1}</span>
                                                )}
                                            </div>
                                            <span className='mt-2 text-xs'>{step.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='space-y-4'>
                        <h3 className='text-lg font-medium'>Order Details</h3>

                        <div className='rounded-lg bg-gray-800 p-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <div className='text-sm text-gray-400'>Order ID</div>
                                    <div className='font-medium'>
                                        {orderId?.slice(0, 8)}...{orderId?.slice(-4)}
                                    </div>
                                </div>
                                <div>
                                    <div className='text-sm text-gray-400'>Status</div>
                                    <div className='font-medium'>{getStatusTitle()}</div>
                                </div>
                            </div>
                        </div>

                        <div className='rounded-lg bg-gray-800 p-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <div className='text-sm text-gray-400'>From</div>
                                    <div className='font-medium'>
                                        {sellAmount} {sellData.token.symbol}
                                    </div>
                                    <div className='mt-1 text-xs text-gray-400'>{sellChainName}</div>
                                </div>
                                <div>
                                    <div className='text-sm text-gray-400'>To</div>
                                    <div className='font-medium'>
                                        {buyAmount} {buyData.token.symbol}
                                    </div>
                                    <div className='mt-1 text-xs text-gray-400'>{buyChainName}</div>
                                </div>
                            </div>
                        </div>

                        {src_deposit_tx && (
                            <div className='rounded-lg bg-gray-800 p-4'>
                                <div className='mb-1 text-sm text-gray-400'>Deposit Transaction</div>
                                <div className='break-all font-medium'>{src_deposit_tx}</div>
                                <div className='mt-2'>
                                    <a
                                        href={`https://explorer.${sellChainName.toLowerCase()}.com/tx/${src_deposit_tx}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-sm text-blue-500 hover:underline'
                                    >
                                        View on Explorer
                                    </a>
                                </div>
                            </div>
                        )}

                        {dst_fill_tx && (
                            <div className='rounded-lg bg-gray-800 p-4'>
                                <div className='mb-1 text-sm text-gray-400'>Destination Transaction</div>
                                <div className='break-all font-medium'>{dst_fill_tx}</div>
                                <div className='mt-2'>
                                    <a
                                        href={`https://explorer.${buyChainName.toLowerCase()}.com/tx/${dst_fill_tx}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-sm text-blue-500 hover:underline'
                                    >
                                        View on Explorer
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className='border-t border-white/10 p-6'>
                    <div className='flex flex-col gap-4'>
                        {[
                            FeatherOrderStatus.DEPOSIT_TIMEOUT,
                            FeatherOrderStatus.TRADE_FAILED,
                            FeatherOrderStatus.WITHDRAWAL_FAILED,
                        ].includes(status) ? (
                            <button
                                onClick={() => alert('TODO: Contact Support')}
                                className='w-full rounded-xl bg-blue-600 px-4 py-3 transition-colors hover:bg-blue-700'
                            >
                                Contact Support
                            </button>
                        ) : (
                            <button
                                onClick={onClose}
                                className='w-full rounded-xl bg-blue-600 px-4 py-3 transition-colors hover:bg-blue-700'
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default FeatherOrderStatusModal
